import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, Check, AlertTriangle, Loader2 } from 'lucide-react'
import { PageShell } from '../../components/layout/PageShell'
import { Field } from '../../components/ui/Field'
import { cancelReservation } from '../../lib/api/booking'
import type { CancelReservationResult } from '../../lib/api/types'
import { cn } from '../../lib/cn'

// Public page (no login required) — cancel a booking with the Reservation Code +
// Cancellation Code that were sent by email. Mirrors the legacy /it/cancelReservation page;
// reuses the existing cancelReservation() API (POST /api/cancelReservation/).
export function CancelReservationPage() {
  const { t } = useTranslation()
  const { locale: routeLocale = 'it' } = useParams()

  const [reservationCode, setReservationCode] = useState('')
  const [cancellationCode, setCancellationCode] = useState('')
  const [touched, setTouched] = useState(false)
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)
  const [result, setResult] = useState<CancelReservationResult | null>(null)

  const codeErr = touched && !reservationCode.trim()
  const cancErr = touched && !cancellationCode.trim()

  async function onSubmit() {
    setTouched(true)
    setFailed(false)
    if (!reservationCode.trim() || !cancellationCode.trim()) return
    setBusy(true)
    try {
      const res = await cancelReservation(reservationCode.trim(), cancellationCode.trim())
      setResult(res)
    } catch {
      setFailed(true)
    } finally {
      setBusy(false)
    }
  }

  function reset() {
    setReservationCode('')
    setCancellationCode('')
    setTouched(false)
    setFailed(false)
    setResult(null)
  }

  return (
    <PageShell>
      <div className="mb-6">
        <h1 className="font-display text-2xl font-extrabold tracking-tight text-ink-900 sm:text-3xl">
          {t('cancel.title')}
        </h1>
        <p className="mt-1.5 text-sm text-ink-500">
          {t('cancel.subtitle')}
        </p>
      </div>

      <div className="rounded-2xl bg-white p-5 shadow-card ring-1 ring-ink-100 sm:p-7">
        {result == null ? (
          <div className="space-y-4">
            <Field
              name="reservationCode"
              label={t('cancel.reservationCode')}
              value={reservationCode}
              onChange={(e) => setReservationCode(e.target.value.toUpperCase())}
              error={codeErr ? t('billing.requiredField') : undefined}
              autoComplete="off"
              autoCapitalize="characters"
            />
            <Field
              name="cancellationCode"
              label={t('cancel.cancellationCode')}
              value={cancellationCode}
              onChange={(e) => setCancellationCode(e.target.value.toUpperCase())}
              error={cancErr ? t('billing.requiredField') : undefined}
              autoComplete="off"
              autoCapitalize="characters"
            />

            {failed && <p className="text-sm font-medium text-brand-600">{t('common.errorGeneric')}</p>}

            <button
              type="button"
              onClick={onSubmit}
              disabled={busy}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand-600 px-4 py-3.5 font-display text-sm font-bold text-white transition hover:bg-brand-700 disabled:opacity-60"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              {t('cancel.submit')}
            </button>
          </div>
        ) : result.cancelled ? (
          <ResultBox
            tone="ok"
            title={t('cancel.okTitle')}
            text={t('cancel.okText')}
            actionLabel={t('common.ok')}
            onAction={reset}
          />
        ) : result.valid !== true ? (
          <ResultBox
            tone="warn"
            title={t('cancel.invalidTitle')}
            text={t('cancel.invalidText')}
            actionLabel={t('common.retry')}
            onAction={reset}
          />
        ) : (
          <ResultBox
            tone="warn"
            title={t('cancel.expiredTitle')}
            text={t('cancel.expiredText')}
            actionLabel={t('common.retry')}
            onAction={reset}
          />
        )}
      </div>

      <Link
        to={`/${routeLocale}`}
        className="mt-7 inline-flex h-11 items-center gap-2 rounded-xl border border-ink-200 px-5 text-sm font-semibold text-ink-700 hover:bg-ink-50"
      >
        <ArrowLeft className="h-4 w-4" /> {t('common.backHome')}
      </Link>
    </PageShell>
  )
}

function ResultBox({
  tone,
  title,
  text,
  actionLabel,
  onAction,
}: {
  tone: 'ok' | 'warn'
  title: string
  text: string
  actionLabel: string
  onAction: () => void
}) {
  const ok = tone === 'ok'
  return (
    <div className="space-y-4">
      <div
        className={cn(
          'flex items-start gap-3 rounded-xl p-4 ring-1',
          ok ? 'bg-green-50 ring-green-200' : 'bg-amber-50 ring-amber-200',
        )}
      >
        <div className={cn('mt-0.5 flex-none', ok ? 'text-green-600' : 'text-amber-600')}>
          {ok ? <Check className="h-5 w-5" /> : <AlertTriangle className="h-5 w-5" />}
        </div>
        <div className="min-w-0">
          <p className={cn('font-display text-sm font-bold', ok ? 'text-green-900' : 'text-amber-900')}>{title}</p>
          <p className={cn('mt-0.5 text-sm leading-snug', ok ? 'text-green-800' : 'text-amber-800')}>{text}</p>
        </div>
      </div>
      <button
        type="button"
        onClick={onAction}
        className="flex w-full items-center justify-center rounded-xl border border-ink-200 px-4 py-3 text-sm font-semibold text-ink-700 transition hover:bg-ink-50"
      >
        {actionLabel}
      </button>
    </div>
  )
}
